import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Chip,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Stack,
  CardMedia,
} from "@mui/material";
import { Tag, Calendar } from "lucide-react";
import Gallery from "./GalleryImages";
import LikeButton from "./LikeCard";
import toast from "react-hot-toast";
import Navbar from "../../User/Navbar";
import Footer from "./Footer";
import Loader from "../../Loader/Loader";
import Logo from "../../../Static/RoundedRTSIcon.png";
import {
  FaLinkedin,
  FaWhatsapp,
  FaTwitter,
  FaInstagram,
  FaFacebook,
} from "react-icons/fa";

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const originalitem = location.state?.originalitem || [];
  const currentUrl = window.location.href;

  useEffect(() => {
    const handleBackButton = (event) => {
      event.preventDefault();
      navigate("/blog");
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [navigate]);

  const fetchAllBlogs = async () => {
    try {
      const response = await fetch(
        "https://rohan-tax-services.onrender.com/api/auth/fetchblog",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const result = await response.json();
      if (response.ok) {
        const reformed = result.reverse();
        setData(reformed);
        sessionStorage.setItem(
          "RohanTaxServicesDetail",
          JSON.stringify(reformed)
        );
      } else {
        toast.error(result.msg || "Failed to fetch blogs");
      }
    } catch (error) {
      toast.error("Error fetching blogs");
    }
  };

  const fetchBlogById = async () => {
    try {
      const response = await fetch(
        `https://rohan-tax-services.onrender.com/api/auth/fetchblog/${id}`,
        { method: "GET" }
      );
      const result = await response.json();
      if (response.ok) {
        setPost(result);
      } else {
        toast.error(result.msg || "Blog not found");
      }
    } catch (error) {
      toast.error("Error fetching blog details");
    } finally {
      setLoading(false);
    }
  };

  const handleViewBlog = (id) => {
    navigate(`/blog/${id}`, {
      replace: true,
      state: { originalitem: data },
    });
  };

  useEffect(() => {
    setLoading(true);
    if (!originalitem.length) {
      const storedData = sessionStorage.getItem("RohanTaxServicesDetail");
      if (storedData) {
        setData(JSON.parse(storedData));
      } else {
        fetchAllBlogs();
      }
    }
    fetchBlogById();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!post) {
    return <div>Blog post not found</div>;
  }

  const relatedArticles = data
    .filter((article) => article._id !== id)
    .slice(0, 3);

  const shareOptions = [
    {
      platform: "Facebook",
      icon: <FaFacebook size={20} />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`,
    },
    {
      platform: "Twitter",
      icon: <FaTwitter size={20} />,
      url: `https://twitter.com/intent/tweet?url=${currentUrl}&text=Check this out!`,
    },
    {
      platform: "LinkedIn",
      icon: <FaLinkedin size={20} />,
      url: `https://www.linkedin.com/shareArticle?mini=true&url=${currentUrl}&title=Check this blog post`,
    },
    {
      platform: "WhatsApp",
      icon: <FaWhatsapp size={20} />,
      url: `https://wa.me/?text=${currentUrl}`,
    },
  ];

  const handleShare = (url) => {
    window.open(url, "_blank");
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    toast.success("Link copied to clipboard!");
  };

  const updateblog = async () => {
    fetchBlogById();
  };

  console.log(post);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-100">
        <div className="container mx-auto px-4 py-8">
          <Card className="overflow-hidden">
            <div className="relative h-[385px] md:h-[385px]">
              <div className="w-full h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 flex items-center justify-center">
                {/* <img
                src={post.image}
                alt="Tax reform concept"
                className="w-full h-96 object-cover brightness-50 pointer-events-none"
              /> */}
                <div className="absolute top-2 right-2 z-10">
                  <img src={Logo} alt="Logo" width={40} height={40} />
                </div>
                <div className="absolute inset-0 flex flex-col justify-end p-8 text-white bg-gradient-to-t from-black/80 to-transparent">
                  <Chip
                    label={post.category}
                    icon={<Tag size={16} />}
                    className="!bg-[#151C62] !text-white mb-4 self-start z-10 !p-4"
                  />
                  <p className="font-bold mb-2 md:text-2xl text-md ">
                    {post.title}
                  </p>
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="flex items-center text-sm">
                        <Calendar size={14} className="mr-2" />
                        {post.date}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
              <main className="lg:col-span-2">
                <CardContent>
                  <Typography
                    variant="body1"
                    className="text-xl leading-relaxed pb-4 whitespace-pre-line text-left"
                  >
                    {post.description}
                  </Typography>
                  {post.galleryImages.length > 0 && (
                    <Gallery images={post.galleryImages} />
                  )}
                  <Divider className="py-4" />
                  <div className="flex items-center justify-between mt-5">
                    <Typography variant="caption" color="textSecondary">
                      Source: {post.source}
                    </Typography>
                    <LikeButton
                      data={post.like}
                      post={post}
                      updateblog={() => {
                        updateblog();
                      }}
                    />
                  </div>
                </CardContent>
              </main>

              <aside className="lg:col-span-1">
                <div className="bg-gray-200 p-6 rounded-lg sticky top-8">
                  <div className="mb-4">
                    <Typography variant="h6" className="!mb-3 font-semibold">
                      Author
                    </Typography>
                    <div className="flex items-center space-x-4">
                      <Avatar alt={"profile"} className="w-10 h-10" />
                      <Typography className="text-xl font-medium">
                        {post.author}
                      </Typography>
                    </div>
                  </div>

                  <Divider className="py-2" />
                  {post.keywords?.[0] &&
                    JSON.parse(post.keywords[0]).length > 0 && (
                      <>
                        <Typography variant="subtitle1" className="py-2">
                          Keywords
                        </Typography>
                        <div className="flex flex-wrap gap-2">
                          {JSON.parse(post.keywords[0]).map(
                            (keyword, index) => (
                              <Chip
                                key={index}
                                label={keyword}
                                className="bg-white text-[#151C62] border-[#151C62]"
                              />
                            )
                          )}
                        </div>
                        <Divider className="py-2" />
                      </>
                    )}
                </div>
                <Divider className="py-4"></Divider>
                <Typography variant="h6" className="!my-4 font-semibold">
                  Share this post:
                </Typography>
                <div className="flex items-center justify-start mb-4 space-x-1">
                  {/* Share Buttons */}
                  {shareOptions.map((option, index) => (
                    <Button
                      key={index}
                      onClick={() => handleShare(option.url)}
                      sx={{
                        color: "#151C62",
                        borderColor: "#151C62",
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "#f4f4f4",
                        },
                      }}
                    >
                      {option.icon}
                    </Button>
                  ))}
                </div>
                <Divider className="py-4" />
                <div>
                  <p className="text-center text-2xl py-2 bold font-serif">
                    Related Articles
                  </p>
                  <Stack spacing={2} className="mt-4">
                    {relatedArticles.map((article) => (
                      <Card key={article._id} className="flex flex-col">
                        <CardMedia
                          component="img"
                          height="140"
                          image={article.image}
                          alt={article.title}
                        />
                        <CardContent>
                          <Typography
                            variant="h8"
                            gutterBottom
                            className="line-clamp-2"
                          >
                            {article.title}
                          </Typography>
                        </CardContent>
                        <Button
                          size="small"
                          onClick={() => handleViewBlog(article._id)}
                          sx={{
                            margin: "0.5rem",
                            color: "#151C62",
                            borderColor: "#151C62", 
                            border : "1px solid ", 
                            borderRadius : "10px"
                          }}
                        >
                          View Blog
                        </Button>
                      </Card>
                    ))}
                  </Stack>
                </div>
              </aside>
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </>
  );
}
